/**
 * @deprecated Use `MoneyInput` from `@krea/shared/design-system/input/money-input` combined with
 * `ControlledWrapper` from `@krea/shared/design-system/input/controlled-wrapper` for React Hook Form integration.
 */
import type { ChangeEvent, FocusEvent, InputHTMLAttributes } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@krea/shared/design-system/Form";
import { cn } from "@krea/shared/utils";

import { FormatNumber, formatToNumber } from "../../utils/formatNumber";
import { Input } from "../inputs/Input";

interface MoneyInputProps<T extends FieldValues>
  extends Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "className" | "placeholder" | "onBlur"
  > {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  currency: string;
  onValueChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

function MoneyInputControl({
  value,
  onChange,
  formOnBlur,
  restField,
  rest,
  onBlur,
  onValueChange,
  invalid,
  isSubmitting,
  currency,
}: {
  value: unknown;
  onChange: (v: number | undefined) => void;
  formOnBlur: () => void;
  restField: Record<string, unknown>;
  rest: Record<string, unknown>;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onValueChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  invalid: boolean;
  isSubmitting: boolean;
  currency: string;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- useFormField is typed in Form.tsx
  const { formItemId } = useFormField();

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full">
        <Input
          {...rest}
          {...restField}
          value={(() => {
            if (value != null && typeof value === "number")
              return FormatNumber(value);
            if (value === 0) return "0";

            return "";
          })()}
          onBlur={(e) => {
            if (onBlur) onBlur(e);
            formOnBlur();
          }}
          id={formItemId}
          error={invalid ? " " : undefined}
          variant={invalid ? "error" : "default"}
          className="min-w-0 flex-1 rounded-r-none"
          onChange={(e) => {
            const num = formatToNumber(e.target.value);
            onChange(num ?? undefined);
            onValueChange?.(e);
          }}
        />
        <div
          className={cn(
            "pointer-events-none flex h-10 shrink-0 items-center justify-center rounded-r-md border border-gray-200 border-l-0 bg-gray-50 px-3 text-sm font-semibold text-black",
            invalid && "!border-red-500",
            isSubmitting && "opacity-50",
          )}
        >
          {currency}
        </div>
      </div>

      <FormMessage />
    </div>
  );
}

export const MoneyInput = <T extends FieldValues>({
  name,
  control,
  required = false,
  currency,
  label,
  onBlur,
  onValueChange,
  ...rest
}: MoneyInputProps<T>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({
        field: { value, onChange, onBlur: formOnBlur, ...restField },
        fieldState: { invalid },
        formState: { isSubmitting },
      }) => (
        <FormItem>
          {label && (
            <FormLabel className="font-bold">
              {label} {required && <span className="text-red-300">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <MoneyInputControl
              value={value}
              onChange={onChange}
              formOnBlur={formOnBlur}
              restField={restField}
              rest={rest}
              onBlur={onBlur}
              onValueChange={onValueChange}
              invalid={invalid}
              isSubmitting={isSubmitting}
              currency={currency}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
